import React from "react";
import "./customTable.css";
import commoncss from "../../../myfin_v2/css/common.module.css"

const MERGE_KEYS = ["date"];

const flatten = (nodes, rows = []) => {
  if (!Array.isArray(nodes)) return rows;

  nodes.forEach((n) => {
    if ((n.level || 0) <= 2) {
      rows.push({
        _id: n._id,
        date: n.date || null,
        particular: n.name || "",
        notes: n.notes || "",
        lineType: n.lineType,
        level: n.level || 0,
        companies: n.companies || [],
        currentYear: n.currentYear,
        previousYear: n.previousYear,
        isBold: n.isBold === true || String(n.isBold).toLowerCase() === "true",
      });
    }

    if (Array.isArray(n.subgroup)) {
      flatten(n.subgroup, rows);
    }
  });

  return rows;
};

const buildRowSpans = (rows) => {
  const map = {};
  const used = {};

  rows.forEach((r) => {
    const key = MERGE_KEYS.filter((k) => r[k])
      .map((k) => r[k])
      .join("||");
    map[key] = (map[key] || 0) + 1;
  });

  return rows.map((r) => {
    const key = MERGE_KEYS.filter((k) => r[k])
      .map((k) => r[k])
      .join("||");

    if (used[key]) return { ...r, _hideMerge: true };
    used[key] = true;
    return { ...r, _rowSpan: map[key] };
  });
};

// ── Constants ────────────────────────────────────────────────────────────────
const COL_PARTICULAR_W = 300;
const COL_NOTES_W      = 70;
const COL_DATE_W       = 100;
const COL_NUMBER_W     = 120;   // CY / PY columns — wide enough to prevent crowding
const ROW1_H           = 38;    // height of the first thead row (company-name row)

const BSPLTable = ({ tableData, redirectTo = () => {}, isPrePopulate = false }) => {
  const flat   = flatten(tableData || []);
  const rows   = buildRowSpans(flat);

  const sample = rows.find((r) => r.companies.length);
  if (!sample) return null;

  const companies = sample.companies.map((c) => c.title);
  const periods   = Object.keys(sample.companies[0]).filter(
    (k) => k !== "title" && !k.startsWith("_")
  );

  const baseColumns = Array.from(
    new Set(
      rows.flatMap((r) =>
        Object.keys(r).filter((k) => {
          if (k.startsWith("_")) return false;
          if (["companies", "level", "lineType", "currentYear", "previousYear", "isBold"].includes(k)) return false;
          if (k === "date") return rows.some((x) => x.date);
          return true;
        })
      )
    )
  );

  const totalCols = baseColumns.length + companies.length * periods.length;

  // ── Sticky-left offsets ──────────────────────────────────────────────────
  const getStickyLeft = (col, colIndex) => {
    if (col === "particular") return 0;
    if (col === "notes") {
      return baseColumns.slice(0, colIndex).reduce((acc, c) => {
        if (c === "particular") return acc + COL_PARTICULAR_W;
        if (c === "date")       return acc + COL_DATE_W;
        return acc;
      }, 0);
    }
    return null;
  };

  const isSticky = (col) => col === "particular" || col === "notes";

  // ── Styles ───────────────────────────────────────────────────────────────
  const styles = {
    tableContainer: {
      overflowX: "auto",
      overflowY: "auto",
      width: "100%",
      maxHeight: isPrePopulate ? "calc(100vh - 95px)" : "calc(88vh - 60px)",
      // Fix: scrollbar appears OUTSIDE the box, not inside → no height shift
      scrollbarGutter: "stable",
      margin: isPrePopulate ? "0px" : undefined,
    },
    table: {
      tableLayout: "fixed",
      borderCollapse: "separate",
      borderSpacing: 0,
      minWidth: "max-content",
      width: "100%",
      marginBottom: isPrePopulate ? "10px" : undefined,
    },

    // ── thead base styles ──────────────────────────────────────────────────
    thParticular: {
      width: `${COL_PARTICULAR_W}px`,
      minWidth: "200px",
      textAlign: "left",
      padding: "8px 10px 8px 14px",
      fontWeight: 600,
      fontSize: "13px",
      height: `${ROW1_H}px`,
      boxSizing: "border-box",
    },
    thNotes: {
      width: `${COL_NOTES_W}px`,
      minWidth: `${COL_NOTES_W}px`,
      maxWidth: `${COL_NOTES_W}px`,
      textAlign: "center",
      padding: "8px 6px",
      fontWeight: 600,
      fontSize: "13px",
      height: `${ROW1_H}px`,
      boxSizing: "border-box",
      borderRight: "1px solid #ccd5d3",
    },
    thDate: {
      width: `${COL_DATE_W}px`,
      minWidth: "90px",
      textAlign: "left",
      padding: "8px 10px",
      fontWeight: 600,
      fontSize: "13px",
      height: `${ROW1_H}px`,
      boxSizing: "border-box",
    },
    // Company-group header (row 1, spans CY+PY)
    thGroup: {
      textAlign: "center",
      padding: "8px 6px",
      fontWeight: 600,
      fontSize: "13px",
      height: `${ROW1_H}px`,
      boxSizing: "border-box",
      position: "sticky",
      top: 0,
      zIndex: 100,
      backgroundColor: "#f0f4f0",
    },
    // CY / PY sub-header (row 2)
    thYear: {
      width: `${COL_NUMBER_W}px`,
      minWidth: "90px",
      textAlign: "right",
      padding: "8px 35px 8px 10px",
      fontWeight: 600,
      fontSize: "13px",
      position: "sticky",
      top: `${ROW1_H}px`,   // exactly below row-1
      zIndex: 90,
      backgroundColor: "#f0f4f0",
      boxSizing: "border-box",
    },

    // ── tbody cell styles ──────────────────────────────────────────────────
    tdParticular: {
      textAlign: "left",
      padding: "7px 10px 7px 14px",
      fontSize: "13px",
      overflow: isPrePopulate ? "visible" : "hidden",
      textOverflow: isPrePopulate ? "clip" : "ellipsis",
      whiteSpace: isPrePopulate ? "normal" : "nowrap",
      wordBreak: isPrePopulate ? "break-word" : undefined,
    },
    tdNotes: {
      width: `${COL_NOTES_W}px`,
      minWidth: `${COL_NOTES_W}px`,
      maxWidth: `${COL_NOTES_W}px`,
      textAlign: "center",
      padding: "7px 6px",
      fontSize: "13px",
    },
    tdDate: {
      width: `${COL_DATE_W}px`,
      minWidth: "90px",
      textAlign: "left",
      padding: "7px 10px",
      fontSize: "13px",
      whiteSpace: "nowrap",
    },
    tdNumber: {
      width: `${COL_NUMBER_W}px`,
      minWidth: "90px",
      textAlign: "right",
      padding: "7px 35px 7px 10px",
      fontSize: "13px",
      whiteSpace: "nowrap",
    },

    headerRow: { backgroundColor: "#f0f4f0" },
    headerCell: {
      textAlign: "left",
      padding: "7px 10px",
      fontWeight: 700,
      fontSize: "13px",
    },
  };

  // ── th style builder ─────────────────────────────────────────────────────
  const groupStyle = isPrePopulate
    ? {
        ...styles.thGroup,
        position: "static",
        top: undefined,
        zIndex: undefined,
        verticalAlign: "middle",
        borderRight: "none",
        borderBottom: "1px solid #ccd5d3",
        paddingLeft: "50px",
        paddingRight: "0px",
      }
    : styles.thGroup;

  const yearStyle = isPrePopulate
    ? {
        ...styles.thYear,
        position: "static",
        top: undefined,
        zIndex: undefined,
        verticalAlign: "middle",
        borderTop: "none",
        borderRight: "none",
        borderBottom: "1px solid #ccd5d3",
        boxShadow: "none",
      }
    : styles.thYear;

  const getThStyle = (col, colIndex) => {
    const base =
      col === "notes" ? { ...styles.thNotes }
      : col === "date" ? { ...styles.thDate }
      : { ...styles.thParticular };

    if (isPrePopulate) {
      return {
        ...base,
        height: "auto",
        zIndex: isSticky(col) ? 110 : 80,
        verticalAlign: "middle",
        borderRight: col === "notes" ? "none" : undefined,
        borderBottom: "1px solid #ccd5d3",
        boxShadow: "none",
        ...(isSticky(col) && {
          position: "sticky",
          left: getStickyLeft(col, colIndex),
          backgroundColor: "#eef6ef",
          boxShadow: "none",
        }),
      };
    }

    return {
      ...base,
      position: "sticky",
      top: 0,
      zIndex: isSticky(col) ? 110 : 80,
      ...(isSticky(col) && {
        left: getStickyLeft(col, colIndex),
        backgroundColor: "#f0f4f0",
      }),
    };
  };

  // ── td style builder ─────────────────────────────────────────────────────
  const getTdStyle = (col, colIndex) => {
    const base =
      col === "notes" ? { ...styles.tdNotes }
      : col === "date" ? { ...styles.tdDate }
      : { ...styles.tdParticular };

    if (!isSticky(col)) return base;

    return {
      ...base,
      position: "sticky",
      left: getStickyLeft(col, colIndex),
      zIndex: 2,
      backgroundColor: "#ffffff",
      // no boxShadow — plain background
    };
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={styles.tableContainer} className="table-container">
      <table className="financial-table" style={styles.table}>
        <thead style={isPrePopulate ? { position: "sticky", top: 0, zIndex: 100, backgroundColor: "#eef6ef" } : undefined}>
          {/* ROW 1 — base columns (rowSpan=2) + company group headers */}
          <tr>
            {baseColumns.map((c, i) => (
              <th
                key={c}
                rowSpan={2}
                className="tableHeadingColor"
                style={getThStyle(c, i)}
              >
                {c.toUpperCase()}
              </th>
            ))}

            {companies.map((c) => (
              <th
                key={c}
                colSpan={periods.length}
                className="tableHeadingColor"
                style={groupStyle}
              >
                {c}
              </th>
            ))}
          </tr>

          {/* ROW 2 — CY / PY sub-headers */}
          <tr>
            {companies.map((c) =>
              periods.map((p) => (
                <th
                  key={`${c}-${p}`}
                  className="tableHeadingColor"
                  style={yearStyle}
                >
                  {p === "currentYear" ? "CY" : "PY"}
                </th>
              ))
            )}
          </tr>
        </thead>

        <tbody>
          {rows.map((row, idx) => {
            // ── Section header row (level === 1) ──────────────────────────
            if (row.level === 1) {
              const hasValues = row.lineType === "formula";
              
              if (!hasValues) {
                return (
                  <tr key={idx} className="row-header" style={styles.headerRow}>
                    <td
                      style={{
                        ...styles.headerCell,
                        position: "sticky",
                        left: 0,
                        zIndex: 2,
                        backgroundColor: "#f0f4f0",
                        paddingLeft: "12px",
                      }}
                      className="col-text header-cell"
                    >
                      {row.particular}
                    </td>
                    <td
                      style={{
                        ...styles.tdNotes,
                        ...styles.headerCell,
                        position: "sticky",
                        left: COL_PARTICULAR_W,
                        zIndex: 2,
                        backgroundColor: "#f0f4f0",
                      }}
                      className="notes-col header-cell"
                    />
                    <td
                      className="header-cell"
                      colSpan={totalCols - 2}
                      style={styles.headerCell}
                    />
                  </tr>
                );
              }

              return (
                <tr key={idx} className="row-header" style={styles.headerRow}>
                  {baseColumns.map((col, i) => {
                    if (MERGE_KEYS.includes(col)) {
                      if (row._hideMerge) return null;
                      return (
                        <td
                          key={col}
                          rowSpan={row._rowSpan}
                          className="col-text header-cell"
                          style={{ 
                            ...getTdStyle(col, i), 
                            ...styles.headerCell, 
                            backgroundColor: "#f0f4f0",
                            ...(col === "particular" && { paddingLeft: "24px" })
                          }}
                        >
                          {row[col]}
                        </td>
                      );
                    }
                    return (
                      <td
                        key={col}
                        className={col === "notes" ? "notes-col header-cell" : "col-text header-cell"}
                        style={{ 
                          ...getTdStyle(col, i), 
                          ...styles.headerCell, 
                          backgroundColor: "#f0f4f0",
                          ...(col === "particular" && { paddingLeft: "24px" })
                        }}
                      >
                        <span
                          className={col === "notes" ? commoncss.clickable : undefined}
                          onClick={() => col === "notes" && redirectTo(row[col], row)}
                          style={
                            col === "notes" && row[col]
                              ? {
                                  color: "#205a46",
                                  fontWeight: 700,
                                  textDecoration: "underline",
                                }
                              : undefined
                          }
                        >
                          {row[col]}
                        </span>
                      </td>
                    );
                  })}
                  {(row.companies && row.companies.length > 0 ? row.companies : companies.map((cTitle, cIdx) => (cIdx === 0 ? { title: cTitle, currentYear: row.currentYear, previousYear: row.previousYear } : { title: cTitle }))).map((c) =>
                    periods.map((p) => (
                      <td
                        key={`${c.title}-${p}`}
                        className="col-number header-cell"
                        style={{ ...styles.headerCell, ...styles.tdNumber, backgroundColor: "#f0f4f0", fontWeight: 700 }}
                      >
                        {c[p] !== null && c[p] !== undefined && c[p] !== ""
                          ? (() => {
                              const num = Math.round(Number(c[p]));
                              if (isNaN(num)) return "";
                              return num < 0
                                ? `(${Math.abs(num).toLocaleString("en-IN")})`
                                : num.toLocaleString("en-IN");
                            })()
                          : ""}
                      </td>
                    ))
                  )}
                </tr>
              );
            }

            // ── Data row ──────────────────────────────────────────────────
            return (
              <tr key={idx} style={{ fontWeight: row.isBold ? 700 : undefined }}>
                {baseColumns.map((col, i) => {
                  // Merged date cell
                  if (MERGE_KEYS.includes(col)) {
                    if (row._hideMerge) return null;
                    return (
                      <td
                        key={col}
                        rowSpan={row._rowSpan}
                        className="col-text"
                        style={{ ...getTdStyle(col, i), ...(row.isBold ? { fontWeight: 700 } : {}) }}
                      >
                        {row[col]}
                      </td>
                    );
                  }

                  return (
                    <td
                      key={col}
                      className={col === "notes" ? "notes-col" : "col-text"}
                      style={{ ...getTdStyle(col, i), ...(row.isBold ? { fontWeight: 700 } : {}) }}
                    >
                      <span
                        className={col === "notes" ? commoncss.clickable : undefined}
                        onClick={() => col === "notes" && redirectTo(row[col], row)}
                        style={col === "notes" && row[col] ? {
                          color: "#205a46",
                          fontWeight: 700,
                          textDecoration: "underline",
                        } : undefined}
                      >
                        {row[col]}
                      </span>
                    </td>
                  );
                })}

                {(row.companies && row.companies.length > 0 ? row.companies : companies.map((cTitle, cIdx) => (cIdx === 0 ? { title: cTitle, currentYear: row.currentYear, previousYear: row.previousYear } : { title: cTitle }))).map((c) =>
                  periods.map((p) => (
                    <td
                      key={`${c.title}-${p}`}
                      className="col-number"
                      style={{ ...styles.tdNumber, ...(row.isBold ? { fontWeight: 700 } : {}) }}
                    >
                      {c[p] !== null && c[p] !== undefined && c[p] !== ""
                        ? (() => {
                            const num = Math.round(Number(c[p]));
                            if (isNaN(num)) return "";
                            return num < 0
                              ? `(${Math.abs(num).toLocaleString("en-IN")})`
                              : num.toLocaleString("en-IN");
                          })()
                        : ""}
                    </td>
                  ))
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default BSPLTable;